import { type FC, useContext } from "react";

import { Button, Icon, toast, useModal } from "@alpacahq/ui/old";

import {
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetOAuthTokens,
} from "~/v2/api/hooks";
import { deleteOAuthToken } from "../../api/oauth";
import { AppDetailsContext } from "./AppDetailsPage";

export const AppDetailsRevoke: FC = () => {
  const { account } = useGetCurrentAccountIfLiveElseTradingAccount();

  const {
    client: { client_id },
  } = useContext(AppDetailsContext);

  // get OAuth token when the account ID is available
  const { tokens } = useGetOAuthTokens({
    accountId: account?.id,
    options: {
      enabled: !!account?.id,
    },
  });

  const revokeApp = async () => {
    // close modal
    toggle();

    const deleteToastId = toast.info("Deleting OAuth token...", {
      autoClose: false,
    });

    // delay for UX 😎
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // filter tokens matching the current client_id
      const token = tokens?.find((t) => t.client_id === client_id);

      if (!token) {
        throw new Error("No OAuth token found.");
      }

      // this revokes the app
      await deleteOAuthToken(token);

      toast.update(deleteToastId, {
        type: "success",
        render: "OAuth token(s) deleted successfully!",
        autoClose: 4000,
      });

      // refresh the page after delay, giving the user time to read the toast
      setTimeout(() => {
        window.location.reload();
      }, 4000);
    } catch (error: unknown) {
      // default error message
      let message = "Error deleting OAuth token.";

      // replace with actual error message if available
      if (error instanceof Error) {
        message = error.message;
      }

      // show error toast
      toast.update(deleteToastId, {
        type: "error",
        render: message,
        autoClose: 4000,
      });
    }
  };

  const { toggle, component: RevokeModal } = useModal({
    title: "Revoke App",
    content: "Are you sure you want to revoke this app?",
    controls: [
      <Button key="cancel" onClick={() => toggle()}>
        Cancel
      </Button>,
      <Button key="ok" onClick={revokeApp} className="bg-red-500 text-white">
        OK
      </Button>,
    ],
  });

  return (
    <>
      <Button onClick={toggle} className="flex bg-rose-500 text-rose-50">
        <Icon name="XCircle" className="mr-2" />
        Revoke App
      </Button>
      {RevokeModal}
    </>
  );
};
